import React, { useEffect, useState } from 'react';

// Tiny non-cryptographic string hash — used only to give the preview
// iframe a key that changes whenever its actual HTML content changes
// (see the iframe below for why that matters), not for anything
// security-sensitive.
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (Math.imul(31, hash) + str.charCodeAt(i)) | 0;
  }
  return hash;
};

// Same client-side substitution the backend applies for real at send time
// (see applyIntroPlaceholders in routes/adminEmail.js) — {{chatLink}}
// can't be a real link here (that requires an actual saved conversation +
// signed token, which this preview deliberately doesn't create), so it's
// shown as an obviously-inert placeholder instead, matching the
// Suggestions preview's own "inert CTA in preview mode" pattern.
// Same small photo-card markup the backend builds for real (see
// buildIntroProfileBlockHtml in routes/adminEmail.js) — kept in sync by
// hand rather than shared, same as the rest of this preview's
// substitution being a client-side mirror, not a live backend call.
const buildProfileBlockHtml = (members) => {
  if (!members || members.length === 0) return '';
  const cards = members.map((m) => {
    const photoHtml = m.photo
      ? `<img src="${m.photo}" alt="" width="72" height="72" style="width:72px;height:72px;border-radius:50%;object-fit:cover;display:block;margin:0 auto;" />`
      : `<div style="width:72px;height:72px;border-radius:50%;background:#f1f5f9;color:#94a3b8;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:1.1rem;margin:0 auto;">?</div>`;
    return `<div style="display:inline-block;width:100px;margin:8px;text-align:center;vertical-align:top;">${photoHtml}<div style="margin-top:6px;font-weight:600;font-size:0.85rem;font-family:-apple-system,sans-serif;">${m.firstName || 'Someone'}</div></div>`;
  }).join('');
  return `<div style="text-align:center;margin:12px 0;">${cards}</div>`;
};

// Same big CTA button the backend builds for real (see CHAT_BUTTON_STYLE /
// {{chatButton_<Label>}} in routes/adminEmail.js) — kept in sync by hand,
// same as the rest of this preview. The link itself is inert here (see
// applyIntroPlaceholders' {{chatLink}} substitution below), same as
// {{chatLink}} used bare.
const CHAT_BUTTON_STYLE = 'display:inline-block;padding:14px 32px;border-radius:8px;text-decoration:none;font-weight:700;font-size:1.05rem;font-family:-apple-system,sans-serif;background:#4338ca;color:#ffffff;';
const CHAT_BUTTON_LABEL_PATTERN = /\{\{chatButton_([^}]+)\}\}/g;

const applyIntroPlaceholders = (str, { firstName, names, suggestedProfileBlock, allProfileBlock }) => {
  const chatLink = '#preview-only-generated-at-send-time';
  let result = String(str || '')
    .replaceAll('{{firstName}}', firstName || 'there')
    .replaceAll('{{names}}', names || '')
    .replaceAll('{{chatLink}}', chatLink)
    .replaceAll('{{suggestedProfileBlock}}', suggestedProfileBlock || '')
    .replaceAll('{{allProfileBlock}}', allProfileBlock || '');

  result = result.replace(CHAT_BUTTON_LABEL_PATTERN, (_match, rawLabel) => {
    const label = rawLabel.trim();
    if (!label) return '';
    return `<div style="text-align:center;margin:16px 0;"><a href="${chatLink}" style="${CHAT_BUTTON_STYLE}">${label}</a></div>`;
  });

  return result;
};

// One tab per actual person who'll receive an email — not per cluster —
// so "who gets the email sent" is as visible here as it is in the
// Suggestions composer's own recipient tabs (SuggestionPreviewTabs.js),
// which this deliberately mirrors the look of. excludedRecipientIds
// (from the Being sent/Not being sent table) drops someone from this list
// entirely — they're still part of the conversation, just not previewed
// here since there's no email of theirs to preview.
const MatchedIntroPreviewTabs = ({ selectedClusters, subject, body, excludedRecipientIds, senderAddress }) => {
  const [activeKey, setActiveKey] = useState('');

  const recipients = selectedClusters.flatMap((cluster, ci) => {
    const clusterId = cluster.roundId ? `${cluster.roundId}` : `cluster${ci}`;
    return cluster.members
      .filter((m) => !excludedRecipientIds?.has(m.userId))
      .map((m) => {
        const otherNames = cluster.members.filter((o) => o.userId !== m.userId).map((o) => o.firstName || 'someone');
        const namesText = otherNames.length <= 1
          ? (otherNames[0] || 'them')
          : `${otherNames.slice(0, -1).join(', ')} and ${otherNames[otherNames.length - 1]}`;
        const otherMembers = cluster.members.filter((o) => o.userId !== m.userId);
        const ctx = {
          firstName: m.firstName,
          names: namesText,
          suggestedProfileBlock: buildProfileBlockHtml(otherMembers),
          allProfileBlock: buildProfileBlockHtml(cluster.members),
        };
        return {
          key: `${clusterId}:${m.userId}`,
          firstName: m.firstName || m.userId,
          email: m.email || '',
          photo: m.photo || null,
          subject: applyIntroPlaceholders(subject, ctx),
          htmlBody: applyIntroPlaceholders(body, ctx),
        };
      });
  });

  useEffect(() => {
    if (recipients.length === 0) { setActiveKey(''); return; }
    if (!recipients.some((r) => r.key === activeKey)) setActiveKey(recipients[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipients.map((r) => r.key).join(',')]);

  if (recipients.length === 0) return null;
  const active = recipients.find((r) => r.key === activeKey) || recipients[0];

  return (
    <div style={{ marginTop: '1rem' }}>
      {senderAddress && <div className="as-email-preview-from">From: {senderAddress}</div>}
      <div className="as-signup-photo-gallery" style={{ margin: '0.4rem 0' }}>
        {recipients.map((r) => (
          r.photo
            ? <img key={r.key} src={r.photo} alt="" className="as-signup-img" title={r.firstName} />
            : <div key={r.key} className="as-signup-img-placeholder" title={r.firstName}>?</div>
        ))}
      </div>
      <div className="as-card-meta" style={{ marginBottom: '0.4rem' }}>Who this will be sent to ({recipients.length}):</div>
      <div className="as-suggestion-recipient-tabs">
        {recipients.map((r) => (
          <button
            key={r.key}
            type="button"
            className={`as-filter-tab ${activeKey === r.key ? 'as-filter-tab--active' : ''}`}
            onClick={() => setActiveKey(r.key)}
          >
            {r.firstName}
          </button>
        ))}
      </div>

      <div className="as-suggestion-recipient-card">
        <div className="as-card-meta"><strong>To:</strong> {active.firstName}{active.email ? ` (${active.email})` : ''}</div>
        <div className="as-card-meta"><strong>Subject:</strong> {active.subject}</div>
        <iframe
          // Keyed on content, not just recipient: some sandboxed iframes
          // (Chrome, at least) don't reliably repaint when only the
          // `srcdoc` attribute is updated on an already-mounted iframe —
          // editing the subject/body was silently leaving the preview
          // blank even though the correct HTML was there in the DOM.
          // Changing the key forces a real remount, which always paints.
          key={`${active.key}:${hashString(active.subject)}:${hashString(active.htmlBody)}`}
          className="as-suggestion-preview-iframe"
          title={`Intro email preview for ${active.firstName}`}
          srcDoc={active.htmlBody}
          sandbox=""
        />
      </div>
    </div>
  );
};

export default MatchedIntroPreviewTabs;
